import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle, Battery, Building2, ChevronDown, ChevronRight,
  Filter, Gauge, MapPin, Radio, Route, Search, X
} from 'lucide-react'
import { Vehicle } from '../types'
import { apiClient } from '../config/api'
import { trackingPresentation, vehicleIconComponent } from '../utils/vehiclePresentation'
import { useVehicles } from '../contexts/VehicleContext'
import TransportAssignmentModal from './TransportAssignmentModal'
import SearchableSelect from './common/SearchableSelect'
import { DateRange, todayRange } from './CalendarModal'
import { cachedPlaceName, resolvePlaceName } from '../utils/placeNames'

interface Props {
  vehicles: Vehicle[]
  selectedVehicle: Vehicle | null
  onVehicleSelect: (vehicle: Vehicle) => void
  onFilteredChange?: (ids: string[]) => void
  onOpenCockpit?: (vehicle: Vehicle) => void
  /** Centre the map on this vehicle, bringing the map forward. */
  onShowOnMap?: (vehicle: Vehicle) => void
  /** The window the dashboard is showing; the activity figures follow it. */
  dateRange?: DateRange
}

/** One vehicle's movement over the window, as computed by the reporting engine. */
interface VehicleActivity {
  distanceKm: number | null
  averageSpeedKmh: number | null
  movingAverageSpeedKmh: number | null
  peakSpeedKmh: number | null
  sampleCount: number
  hasGps: boolean
  hasSpeed: boolean
  reason: 'NOT_TRACKED' | 'NO_DATA_IN_RANGE' | null
}

interface FleetRow {
  id: number
  name: string
  customer_name?: string
}

interface ActiveAlert {
  id: string | number
  vehicle_source?: string
  vehicle_id?: string | number
  rule_name?: string
  alert_type?: string
  category?: string
  message?: string
}

type TrackingFilter = 'ALL' | 'LIVE' | 'STALE' | 'OFFLINE' | 'NOT_TRACKED' | 'LOW_BATTERY'

const hasPosition = (vehicle: Vehicle) => {
  const { latitude, longitude } = vehicle.location || {}
  return Number.isFinite(latitude) && Number.isFinite(longitude) &&
    !(latitude === 0 && longitude === 0)
}

/** Colour and wording both follow the speed: parked and driving are not alike. */
const presentationFor = (vehicle: Vehicle) =>
  trackingPresentation(vehicle.trackingStatus, vehicle.currentSpeed)

const relativeTime = (date: Date) => {
  const value = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(value.getTime())) return 'No telemetry'
  const minutes = Math.max(0, Math.floor((Date.now() - value.getTime()) / 60000))
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  return `${Math.floor(hours / 24)} d ago`
}

/** departure → destination line + slim route-progress bar on the card */
const TransportSummary: React.FC<{ vehicle: Vehicle }> = ({ vehicle }) => {
  const transport = vehicle.transport
  const departure = transport?.departure || vehicle.containerInfo?.origin
  const destination = transport?.destination || vehicle.containerInfo?.destination
  if (!departure && !destination && !transport?.corridorName) return null
  const progress = transport?.progress
  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center justify-between gap-2 text-[11px]">
        <span className="inline-flex items-center gap-1 min-w-0 text-slate-600">
          <Route size={12} className="flex-shrink-0 text-blue-500" />
          <span className="truncate font-medium" title={transport?.corridorName || undefined}>
            {departure || '—'} <span className="text-slate-400">→</span> {destination || '—'}
          </span>
        </span>
        {progress != null && (
          <span className="font-semibold text-blue-600 flex-shrink-0">{progress}%</span>
        )}
      </div>
      {progress != null && (
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${Math.max(2, progress)}%` }} />
        </div>
      )}
    </div>
  )
}

/** /api/vehicles/activity keys vehicles as ASSET:12; the list uses ASSET-12. */
const activityKey = (vehicle: Vehicle) => vehicle.id.replace('-', ':')

/** Only the identity rows that hold something: empty labels taught nothing. */
const detailRows = (vehicle: Vehicle): Array<[string, string]> => {
  const rows: Array<[string, string]> = []
  const type = vehicle.assetInfo?.type || vehicle.containerInfo?.type
  const brand = [vehicle.assetInfo?.brand, vehicle.assetInfo?.model].filter(Boolean).join(' ')
  const transport = vehicle.transport?.corridorName ||
    [vehicle.transport?.departure, vehicle.transport?.destination].filter(Boolean).join(' → ')
  if (type) rows.push(['Type', type])
  if (brand) rows.push(['Brand / model', brand])
  if (vehicle.baliseCodes?.length) rows.push(['Balise', vehicle.baliseCodes.join(', ')])
  if (transport) rows.push(['Transport', transport])
  return rows
}

/**
 * Where the vehicle is, named rather than numbered, and clickable: coordinates
 * are exact and useless to a dispatcher, who wants "Baco Djikoroni" and then
 * wants to see it on the map. The coordinates stay underneath for the record.
 */
const VehiclePositionRow: React.FC<{
  vehicle: Vehicle
  onShowOnMap?: (vehicle: Vehicle) => void
}> = ({ vehicle, onShowOnMap }) => {
  const latitude = vehicle.location.latitude
  const longitude = vehicle.location.longitude
  const coordinates = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
  const [place, setPlace] = useState<string | null>(() => cachedPlaceName(latitude, longitude))

  useEffect(() => {
    let cancelled = false
    resolvePlaceName(latitude, longitude).then(name => {
      if (!cancelled) setPlace(name)
    })
    return () => { cancelled = true }
  }, [latitude, longitude])

  return (
    <button
      type="button"
      onClick={() => onShowOnMap?.(vehicle)}
      disabled={!onShowOnMap}
      title={onShowOnMap ? `${coordinates} — show on the map` : coordinates}
      className={`w-full flex justify-between gap-3 text-left rounded-md px-1.5 py-1 ${
        onShowOnMap ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' : 'cursor-default'
      }`}
    >
      <span className={`inline-flex items-center gap-1 flex-shrink-0 ${onShowOnMap ? 'text-blue-600' : 'text-slate-500'}`}>
        <MapPin size={11} className={onShowOnMap ? 'text-blue-500' : 'text-slate-400'} />
        Position
      </span>
      <span className="min-w-0 text-right">
        <span className={`block truncate ${onShowOnMap ? 'font-semibold' : 'font-medium'}`}>{place || coordinates}</span>
        {place && <span className="block text-[10px] text-slate-400">{coordinates}</span>}
      </span>
    </button>
  )
}

const ACTIVITY_REASONS: Record<string, string> = {
  NOT_TRACKED: 'no tracker fitted',
  NO_DATA_IN_RANGE: 'nothing received in this period'
}

/**
 * What the vehicle did over the window: the figures Nana asked for in place of
 * the identity rows. A value that cannot be measured says why instead of
 * printing a zero that reads like "it did not move".
 */
const VehicleActivityBlock: React.FC<{
  activity?: VehicleActivity
  state: 'idle' | 'loading' | 'ready' | 'error'
  label?: string
}> = ({ activity, state, label }) => {
  const missing = (reason: string) => (
    <span className="text-slate-400">— <span className="text-[10px]">({reason})</span></span>
  )
  const figure = (value: number | null, unit: string) => {
    if (value != null) return <span className="font-semibold text-slate-800">{value} {unit}</span>
    if (!activity) return null
    if (activity.reason) return missing(ACTIVITY_REASONS[activity.reason] || activity.reason)
    return missing('not reported by this tracker')
  }
  // The moving average is null when the tracker reported but never went above
  // the stop threshold, which is a different fact from "no speed reported".
  const movingSpeed = () => {
    if (activity && activity.movingAverageSpeedKmh == null && !activity.reason && activity.hasSpeed) {
      return missing('did not move in this period')
    }
    return figure(activity?.movingAverageSpeedKmh ?? null, 'km/h')
  }

  return (
    <div className="pb-2 mb-1 border-b border-slate-200 space-y-1">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {label || 'Selected period'}
      </div>
      {state === 'loading' && <div className="text-slate-400">Measuring…</div>}
      {state === 'error' && <div className="text-amber-600">Activity unavailable</div>}
      {state === 'ready' && !activity && <div className="text-slate-400">No activity recorded</div>}
      {activity && (
        <>
          <div className="flex justify-between gap-3">
            <span className="text-slate-500">Distance covered</span>
            {figure(activity.distanceKm, 'km')}
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-slate-500">Average speed while moving</span>
            {movingSpeed()}
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-slate-500">Peak speed</span>
            {figure(activity.peakSpeedKmh, 'km/h')}
          </div>
        </>
      )}
    </div>
  )
}

const VehicleTypeIcon: React.FC<{ vehicle: Vehicle }> = ({ vehicle }) => {
  const Icon = vehicleIconComponent(
    vehicle.iconKey,
    vehicle.assetInfo?.type,
    vehicle.vehicleSource === 'CONTAINER' || Boolean(vehicle.containerInfo)
  )
  return <Icon size={18} className={presentationFor(vehicle).icon} aria-hidden="true" />
}

const FleetVehicleExplorer: React.FC<Props> = ({
  vehicles, selectedVehicle, onVehicleSelect, onFilteredChange, onOpenCockpit,
  onShowOnMap, dateRange
}) => {
  const [search, setSearch] = useState('')
  const [vehiclePick, setVehiclePick] = useState('')
  const [tracking, setTracking] = useState<TrackingFilter>('ALL')
  const [moreOpen, setMoreOpen] = useState(false)
  const [customer, setCustomer] = useState('ALL')
  const [fleet, setFleet] = useState('ALL')
  const [source, setSource] = useState('ALL')
  const [vehicleType, setVehicleType] = useState('ALL')
  const [balise, setBalise] = useState('ALL')
  const [alertMode, setAlertMode] = useState('ALL')
  const [alertType, setAlertType] = useState('ALL')
  const [itinerary, setItinerary] = useState('ALL')
  const [departure, setDeparture] = useState('ALL')
  const [destination, setDestination] = useState('ALL')
  const [bookingRef, setBookingRef] = useState('ALL')
  const [containerOwner, setContainerOwner] = useState('ALL')
  const [fleets, setFleets] = useState<FleetRow[]>([])
  const [memberships, setMemberships] = useState<Map<string, number[]>>(new Map())
  const [alerts, setAlerts] = useState<ActiveAlert[]>([])
  const [expandedVehicle, setExpandedVehicle] = useState<string | null>(null)
  const [transportVehicle, setTransportVehicle] = useState<Vehicle | null>(null)
  const [activity, setActivity] = useState<Record<string, VehicleActivity>>({})
  const [activityState, setActivityState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const { loadVehicles } = useVehicles()
  const { t } = useTranslation()

  const trackingText = (vehicle: Vehicle) => {
    const presentation = presentationFor(vehicle)
    return t(presentation.labelKey, { defaultValue: presentation.label })
  }

  const window24h = useMemo(() => dateRange || todayRange(), [dateRange])
  const activityFrom = window24h.startAt || window24h.startDate
  const activityTo = window24h.endAt || window24h.endDate

  // Distance and speed come from the reporting engine, so a card and a report
  // never disagree. Loaded on the first card someone opens rather than with the
  // list: it reads telemetry for the whole window.
  //
  // The window being loaded is held in a ref rather than in the state the effect
  // watches: setting the state to 'loading' would re-run the effect and its
  // cleanup would cancel the request it had just made.
  const activityWindowRef = useRef<string | null>(null)

  useEffect(() => {
    activityWindowRef.current = null
    setActivity({})
    setActivityState('idle')
  }, [activityFrom, activityTo])

  useEffect(() => {
    if (!expandedVehicle) return
    const key = `${activityFrom}|${activityTo}`
    if (activityWindowRef.current === key) return
    activityWindowRef.current = key
    setActivityState('loading')
    apiClient.get('/vehicles/activity', { from: activityFrom, to: activityTo })
      .then(response => {
        if (activityWindowRef.current !== key) return
        setActivity(response.data?.vehicles || {})
        setActivityState('ready')
      })
      .catch(() => {
        if (activityWindowRef.current === key) setActivityState('error')
      })
  }, [expandedVehicle, activityFrom, activityTo])

  useEffect(() => {
    Promise.all([
      apiClient.get('/fleets').catch(() => ({ data: [] })),
      apiClient.get('/fleets/memberships').catch(() => ({ data: [] }))
    ]).then(([fleetResponse, membershipResponse]) => {
      setFleets(Array.isArray(fleetResponse.data) ? fleetResponse.data : fleetResponse.data?.fleets || [])
      const next = new Map<string, number[]>()
      for (const row of (Array.isArray(membershipResponse.data) ? membershipResponse.data : [])) {
        const key = `${row.vehicle_source}-${row.vehicle_id}`
        next.set(key, [...(next.get(key) || []), Number(row.fleet_id)])
      }
      setMemberships(next)
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    const loadAlerts = () => apiClient.get('/alerts', {
      status: 'ACTIVE,ACKNOWLEDGED',
      limit: 1000
    }).then(response => {
      if (!cancelled) {
        setAlerts(Array.isArray(response.data) ? response.data : response.data?.alerts || [])
      }
    }).catch(() => {
      if (!cancelled) setAlerts([])
    })
    loadAlerts()
    const timer = window.setInterval(loadAlerts, 30_000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [])

  const customers = useMemo(() => Array.from(new Map(
    vehicles
      .filter(vehicle => vehicle.customerInfo?.name)
      .map(vehicle => [vehicle.customerInfo!.code || vehicle.customerInfo!.name!, {
        value: vehicle.customerInfo!.name!,
        code: vehicle.customerInfo!.code || ''
      }])
  ).values()).sort((a, b) => a.value.localeCompare(b.value)), [vehicles])

  const customerScoped = useMemo(() => customer === 'ALL'
    ? vehicles
    : vehicles.filter(vehicle => vehicle.customerInfo?.name === customer), [vehicles, customer])

  const types = useMemo(() => Array.from(new Set(customerScoped
    .map(vehicle => vehicle.assetInfo?.type || vehicle.containerInfo?.type)
    .filter(Boolean) as string[])).sort(), [customerScoped])

  const balises = useMemo(() => Array.from(new Set(customerScoped
    .flatMap(vehicle => vehicle.baliseCodes || []))).sort(), [customerScoped])

  // Transport filter options: only values that actually appear on vehicles
  const itineraries = useMemo(() => Array.from(new Map(customerScoped
    .filter(vehicle => vehicle.transport?.corridorId)
    .map(vehicle => [vehicle.transport!.corridorId!, vehicle.transport!.corridorName || vehicle.transport!.corridorId!])
  ).entries()).sort((a, b) => a[1].localeCompare(b[1])), [customerScoped])
  const departures = useMemo(() => Array.from(new Set(customerScoped
    .map(vehicle => vehicle.transport?.departure || vehicle.containerInfo?.origin)
    .filter(Boolean) as string[])).sort(), [customerScoped])
  const destinations = useMemo(() => Array.from(new Set(customerScoped
    .map(vehicle => vehicle.transport?.destination || vehicle.containerInfo?.destination)
    .filter(Boolean) as string[])).sort(), [customerScoped])
  const bookingRefs = useMemo(() => Array.from(new Set(customerScoped
    .map(vehicle => vehicle.containerInfo?.bookingReference)
    .filter(Boolean) as string[])).sort(), [customerScoped])
  const containerOwners = useMemo(() => Array.from(new Set(customerScoped
    .map(vehicle => vehicle.containerInfo?.owner)
    .filter(Boolean) as string[])).sort(), [customerScoped])

  const alertKey = (alert: ActiveAlert) =>
    alert.vehicle_source && alert.vehicle_id != null
      ? `${String(alert.vehicle_source).toUpperCase()}-${alert.vehicle_id}`
      : ''
  const alertsByVehicle = useMemo(() => {
    const map = new Map<string, ActiveAlert[]>()
    alerts.forEach(alert => {
      const key = alertKey(alert)
      if (key) map.set(key, [...(map.get(key) || []), alert])
    })
    return map
  }, [alerts])
  const alertTypes = useMemo(() => Array.from(new Set(alerts
    .map(alert => alert.rule_name || alert.alert_type || alert.category)
    .filter(Boolean) as string[])).sort(), [alerts])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    return vehicles.filter(vehicle => {
      const memberFleetIds = memberships.get(vehicle.id) || []
      const activeAlerts = alertsByVehicle.get(vehicle.id) || []
      const type = vehicle.assetInfo?.type || vehicle.containerInfo?.type || ''
      const searchValues = [
        vehicle.assetInfo?.code, vehicle.containerInfo?.name, vehicle.name,
        vehicle.plateNumber, vehicle.customerInfo?.name, vehicle.customerInfo?.code,
        vehicle.containerInfo?.bookingReference, vehicle.containerInfo?.equipmentReference,
        vehicle.transport?.corridorName, vehicle.transport?.departure, vehicle.transport?.destination,
        ...(vehicle.baliseCodes || []),
        ...memberFleetIds.map(id => fleets.find(item => item.id === id)?.name)
      ].filter(Boolean).map(String)

      const trackingMatch = tracking === 'ALL' ||
        (tracking === 'LOW_BATTERY'
          ? vehicle.baliseBattery != null && vehicle.baliseBattery < 20
          : vehicle.trackingStatus === tracking)
      const alertMatch = alertMode === 'ALL' ||
        (alertMode === 'ACTIVE' && activeAlerts.length > 0) ||
        (alertMode === 'NONE' && activeAlerts.length === 0)
      const alertTypeMatch = alertType === 'ALL' || activeAlerts.some(alert =>
        (alert.rule_name || alert.alert_type || alert.category) === alertType)

      const vehicleDeparture = vehicle.transport?.departure || vehicle.containerInfo?.origin || ''
      const vehicleDestination = vehicle.transport?.destination || vehicle.containerInfo?.destination || ''

      return (!query || searchValues.some(value => value.toLowerCase().includes(query))) &&
        (vehiclePick === '' || vehicle.id === vehiclePick) &&
        trackingMatch &&
        (customer === 'ALL' || vehicle.customerInfo?.name === customer) &&
        (fleet === 'ALL' || memberFleetIds.includes(Number(fleet))) &&
        (source === 'ALL' || vehicle.vehicleSource === source) &&
        (vehicleType === 'ALL' || type === vehicleType) &&
        (balise === 'ALL' || vehicle.baliseCodes?.includes(balise)) &&
        (itinerary === 'ALL' || vehicle.transport?.corridorId === itinerary) &&
        (departure === 'ALL' || vehicleDeparture === departure) &&
        (destination === 'ALL' || vehicleDestination === destination) &&
        (bookingRef === 'ALL' || vehicle.containerInfo?.bookingReference === bookingRef) &&
        (containerOwner === 'ALL' || vehicle.containerInfo?.owner === containerOwner) &&
        alertMatch && alertTypeMatch
    })
  }, [
    vehicles, memberships, alertsByVehicle, fleets, search, tracking, customer,
    fleet, source, vehicleType, balise, alertMode, alertType, vehiclePick,
    itinerary, departure, destination, bookingRef, containerOwner
  ])

  const mapVehicles = filtered
  const mapSignature = mapVehicles.map(vehicle => vehicle.id).sort().join(',')
  useEffect(() => {
    onFilteredChange?.(mapVehicles.map(vehicle => vehicle.id))
  }, [mapSignature])

  const clearFilters = () => {
    setSearch('')
    setVehiclePick('')
    setTracking('ALL')
    setCustomer('ALL')
    setFleet('ALL')
    setSource('ALL')
    setVehicleType('ALL')
    setBalise('ALL')
    setAlertMode('ALL')
    setAlertType('ALL')
    setItinerary('ALL')
    setDeparture('ALL')
    setDestination('ALL')
    setBookingRef('ALL')
    setContainerOwner('ALL')
  }

  const activeFilterCount = [
    tracking, customer, fleet, source, vehicleType, balise, alertMode, alertType,
    itinerary, departure, destination, bookingRef, containerOwner
  ].filter(value => value !== 'ALL').length

  const vehicleOptions = useMemo(() => vehicles
    .map(vehicle => ({
      value: vehicle.id,
      label: vehicle.name,
      hint: [vehicle.plateNumber, vehicle.customerInfo?.name, ...(vehicle.baliseCodes || [])]
        .filter(Boolean).join(' · ') || null
    }))
    .sort((a, b) => a.label.localeCompare(b.label)), [vehicles])

  const searchAssets = search.trim() ? filtered.slice(0, 4) : []
  const searchCustomers = search.trim()
    ? customers.filter(item =>
        item.value.toLowerCase().includes(search.toLowerCase()) ||
        item.code.toLowerCase().includes(search.toLowerCase())).slice(0, 3)
    : []

  return (
    <div className="h-full flex flex-col bg-[#f7f8fa] text-slate-800">
      <div className="p-3 border-b border-slate-200 bg-white space-y-3">
        <div className="relative">
          <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Find asset, customer, plate or balise"
            className="w-full h-10 pl-10 pr-9 rounded-xl border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              <X size={15} />
            </button>
          )}
        </div>

        {search && (searchAssets.length > 0 || searchCustomers.length > 0) && (
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-2 space-y-2">
            {searchAssets.length > 0 && (
              <div>
                <div className="px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Assets & containers</div>
                {searchAssets.map(vehicle => (
                  <button key={vehicle.id} onClick={() => onVehicleSelect(vehicle)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-blue-50">
                    <VehicleTypeIcon vehicle={vehicle} />
                    <span className="min-w-0">
                      <span className="block text-xs font-semibold truncate">{vehicle.name}</span>
                      <span className="block text-[10px] text-slate-500 truncate">{vehicle.customerInfo?.name || 'Unassigned'}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            {searchCustomers.length > 0 && (
              <div>
                <div className="px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Customers</div>
                <div className="flex flex-wrap gap-1.5">
                  {searchCustomers.map(item => (
                    <button key={item.value} onClick={() => { setCustomer(item.value); setSearch('') }}
                      className="px-2 py-1 rounded-lg bg-slate-100 text-[11px] font-medium hover:bg-blue-100">
                      {item.value}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* The vehicle picker is the only filter on show: people arrive here
            looking for one vehicle, and six always-open selects hid the list.
            Everything else lives under "More filters". */}
        <SearchableSelect
          options={vehicleOptions}
          value={vehiclePick}
          onChange={value => {
            setVehiclePick(value)
            const picked = vehicles.find(item => item.id === value)
            if (picked) onVehicleSelect(picked)
          }}
          placeholder="All vehicles"
          searchPlaceholder="Type a name, plate or balise"
          clearText="All vehicles"
          emptyText="No vehicle matches"
        />

        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">{filtered.length} of {vehicles.length} vehicles</span>
          <div className="flex items-center gap-2">
            {(activeFilterCount > 0 || vehiclePick) && (
              <button onClick={clearFilters} className="text-[11px] font-semibold text-blue-600">Clear all</button>
            )}
            <button onClick={() => setMoreOpen(value => !value)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold ${
                moreOpen || activeFilterCount > 0 ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-slate-200 text-slate-600'
              }`}>
              <Filter size={14} /> More filters
              {activeFilterCount > 0 && <span className="w-4 h-4 rounded-full bg-blue-600 text-white text-[9px] flex items-center justify-center">{activeFilterCount}</span>}
            </button>
          </div>
        </div>

        {moreOpen && (
        <div className="space-y-2 p-3 rounded-xl border border-slate-200 bg-slate-50">
        <div className="grid grid-cols-2 gap-1.5">
          <select value={customer} onChange={event => {
            setCustomer(event.target.value)
            setVehicleType('ALL')
            setBalise('ALL')
          }} className={`col-span-2 h-9 px-2 rounded-lg border text-xs font-medium ${
            customer !== 'ALL' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-700'
          }`}>
            <option value="ALL">All customers</option>
            {customers.map(item => <option key={item.value} value={item.value}>{item.value} {item.code ? `(${item.code})` : ''}</option>)}
          </select>
          <select value={fleet} onChange={event => setFleet(event.target.value)}
            className={`h-9 px-2 rounded-lg border text-xs font-medium ${
              fleet !== 'ALL' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-700'
            }`}>
            <option value="ALL">All fleets</option>
            {fleets.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <select value={alertMode} onChange={event => {
            setAlertMode(event.target.value)
            if (event.target.value !== 'ACTIVE') setAlertType('ALL')
          }} className={`h-9 px-2 rounded-lg border text-xs font-medium ${
            alertMode !== 'ALL' ? 'border-red-300 bg-red-50 text-red-700' : 'border-slate-300 bg-white text-slate-700'
          }`}>
            <option value="ALL">All alert states</option>
            <option value="ACTIVE">Has active alert</option>
            <option value="NONE">No active alert</option>
          </select>
          {alertMode === 'ACTIVE' && (
            <select value={alertType} onChange={event => setAlertType(event.target.value)}
              className="col-span-2 h-9 px-2 rounded-lg border border-red-200 bg-white text-xs font-medium text-slate-700">
              <option value="ALL">All active alert types</option>
              {alertTypes.map(type => <option key={type} value={type}>{type}</option>)}
            </select>
          )}
        </div>

        {/* Transport filters (CMA-style): itinerary, departure, destination */}
        {(itineraries.length > 0 || departures.length > 0 || destinations.length > 0) && (
          <div className="grid grid-cols-2 gap-1.5">
            <select value={itinerary} onChange={event => setItinerary(event.target.value)}
              className={`col-span-2 h-9 px-2 rounded-lg border text-xs font-medium ${
                itinerary !== 'ALL' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-700'
              }`}>
              <option value="ALL">All itineraries</option>
              {itineraries.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
            <select value={departure} onChange={event => setDeparture(event.target.value)}
              className={`h-9 px-2 rounded-lg border text-xs font-medium ${
                departure !== 'ALL' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-700'
              }`}>
              <option value="ALL">All departures</option>
              {departures.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
            <select value={destination} onChange={event => setDestination(event.target.value)}
              className={`h-9 px-2 rounded-lg border text-xs font-medium ${
                destination !== 'ALL' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-700'
              }`}>
              <option value="ALL">All destinations</option>
              {destinations.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
        )}

            <div>
              <div className="pb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Tracking status</div>
              <div className="flex flex-wrap gap-1.5">
                {([
                  ['ALL', 'All'], ['LIVE', 'Transmitting'], ['STALE', 'No signal'], ['OFFLINE', 'Offline'],
                  ['NOT_TRACKED', 'Not tracked'], ['LOW_BATTERY', 'Low battery']
                ] as Array<[TrackingFilter, string]>).map(([value, label]) => (
                  <button key={value} onClick={() => setTracking(value)}
                    className={`px-2.5 py-1.5 rounded-full border text-[11px] font-semibold whitespace-nowrap ${
                      tracking === value ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-600'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={source} onChange={event => setSource(event.target.value)}
                className="h-9 px-2 rounded-lg border border-slate-300 bg-white text-xs">
                <option value="ALL">Assets & containers</option>
                <option value="ASSET">Assets only</option>
                <option value="CONTAINER">Containers only</option>
              </select>
              <select value={vehicleType} onChange={event => setVehicleType(event.target.value)}
                className="h-9 px-2 rounded-lg border border-slate-300 bg-white text-xs">
                <option value="ALL">All types</option>
                {types.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            <select value={balise} onChange={event => setBalise(event.target.value)}
              className="h-9 px-2 rounded-lg border border-slate-300 bg-white text-xs">
              <option value="ALL">All balises</option>
              {balises.map(code => <option key={code} value={code}>{code}</option>)}
            </select>
            {(bookingRefs.length > 0 || containerOwners.length > 0) && (
              <div className="grid grid-cols-2 gap-2">
                <select value={bookingRef} onChange={event => setBookingRef(event.target.value)}
                  className="h-9 px-2 rounded-lg border border-slate-300 bg-white text-xs">
                  <option value="ALL">All booking refs</option>
                  {bookingRefs.map(ref => <option key={ref} value={ref}>{ref}</option>)}
                </select>
                <select value={containerOwner} onChange={event => setContainerOwner(event.target.value)}
                  className="h-9 px-2 rounded-lg border border-slate-300 bg-white text-xs">
                  <option value="ALL">All container owners</option>
                  {containerOwners.map(owner => <option key={owner} value={owner}>{owner}</option>)}
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fleet groups used to head this list as collapsible cards; the
          customer wanted a flat vehicle list (the Fleet filter above still
          narrows it), so the list is vehicles only. */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {filtered.map(vehicle => {
          const expanded = expandedVehicle === vehicle.id
          const activeAlerts = alertsByVehicle.get(vehicle.id) || []
          return (
            <article key={vehicle.id}
              className={`rounded-xl border bg-white transition-shadow ${
                selectedVehicle?.id === vehicle.id ? 'border-blue-300 ring-2 ring-blue-100 shadow-md' : 'border-slate-200'
              }`}>
              <button onClick={() => onVehicleSelect(vehicle)} className="w-full p-3 text-left">
                <div className="flex items-start gap-2.5">
                  <span className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                    <VehicleTypeIcon vehicle={vehicle} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="font-bold text-sm truncate">{vehicle.name}</span>
                      {activeAlerts.length > 0 && <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />}
                    </span>
                    <span className="block text-[11px] text-slate-500 truncate">
                      {vehicle.operationalIdentifier || 'No identifier'}
                      {' · '}{vehicle.customerInfo?.name || 'Unassigned'}
                    </span>
                  </span>
                  <span className={`px-2 py-1 rounded-full border text-[10px] font-bold ${presentationFor(vehicle).badge}`}>
                    {trackingText(vehicle)}
                  </span>
                </div>
                <div className="mt-2.5 flex items-center justify-between gap-2 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1 min-w-0">
                    {hasPosition(vehicle) ? <MapPin size={12} /> : <Radio size={12} />}
                    <span className="truncate">{hasPosition(vehicle) ? relativeTime(vehicle.lastUpdate) : 'No GPS position'}</span>
                  </span>
                  {vehicle.baliseBattery != null && (
                    <span className={`inline-flex items-center gap-1 font-semibold ${vehicle.baliseBattery < 20 ? 'text-red-600' : 'text-slate-600'}`}>
                      <Battery size={12} /> {vehicle.baliseBattery}%
                    </span>
                  )}
                </div>
                <TransportSummary vehicle={vehicle} />
              </button>
              <button onClick={() => setExpandedVehicle(expanded ? null : vehicle.id)}
                className="w-full px-3 py-1.5 border-t border-slate-100 flex items-center justify-between text-[10px] font-semibold text-slate-500 hover:bg-slate-50">
                Details
                {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              </button>
              {expanded && (
                <div className="px-3 py-2.5 bg-slate-50 border-t border-slate-100 space-y-1.5 text-[11px]">
                  <VehicleActivityBlock
                    activity={activity[activityKey(vehicle)]}
                    state={activityState}
                    label={window24h.label}
                  />
                  {hasPosition(vehicle) && (
                    <VehiclePositionRow vehicle={vehicle} onShowOnMap={onShowOnMap} />
                  )}
                  {detailRows(vehicle).map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-3">
                      <span className="text-slate-500">{label}</span>
                      <span className="font-medium truncate">{value}</span>
                    </div>
                  ))}
                  {activeAlerts.length > 0 && (
                    <div className="pt-1.5 border-t border-slate-200 text-red-600">
                      <AlertTriangle size={12} className="inline mr-1" />
                      {activeAlerts.map(alert => alert.rule_name || alert.alert_type || alert.category || 'Active alert').join(', ')}
                    </div>
                  )}
                  <button type="button" onClick={() => setTransportVehicle(vehicle)}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100">
                    <Route size={13} /> {vehicle.transport ? 'Edit transport' : 'Assign transport'}
                  </button>
                  {onOpenCockpit && (
                    <button type="button" onClick={() => onOpenCockpit(vehicle)}
                      className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700">
                      <Gauge size={13} /> Open vehicle cockpit
                    </button>
                  )}
                </div>
              )}
            </article>
          )
        })}

        {filtered.length === 0 && (
          <div className="py-12 text-center text-slate-500">
            <Building2 size={28} className="mx-auto mb-2 text-slate-300" />
            <div className="text-sm font-semibold">No matching vehicles</div>
            <button onClick={clearFilters} className="mt-2 text-xs text-blue-600 font-semibold">Clear filters</button>
          </div>
        )}
      </div>

      {transportVehicle && (
        <TransportAssignmentModal
          vehicle={transportVehicle}
          onClose={() => setTransportVehicle(null)}
          onSaved={() => { loadVehicles().catch(() => {}) }}
        />
      )}
    </div>
  )
}

export default FleetVehicleExplorer
